import { Typography } from "@/components/atoms/Typography";
import { ChevronRightIcon } from "@/components/atoms/icons/ChevronRightIcon";
import { Skeleton } from "@/components/atoms/skeleton/Skeleton";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { Platform } from "react-native";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useI18nContext, useIsAdhaJourney, useTranslation, useUIColors } from "@/core/hooks";
import { FAQScreenWeb } from "@/modules/faq/components/FAQScreenWeb";
import { FAQEmptyState } from "@/modules/faq/components/FAQEmptyState";
import { FAQSearchInput } from "@/modules/faq/components/FAQSearchInput";
import { FaqList } from "@/modules/faq/components/FaqList";
import { useFaqs } from "@/modules/faq/hooks/useFaqs";
import { searchFaqItems } from "@/modules/faq/utils/faqSearch";
import { useRouter } from "expo-router";
import React, { useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, StyleSheet, TextInput, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export default function FAQScreen() {
  // Use WebAppLayout on web platform
  if (Platform.OS === "web") {
    return <FAQScreenWeb />;
  }

  // Native implementation
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<TextInput>(null);
  // Same "State Awareness" as FAQScreenWeb — active ADHA sessions get the
  // ADHA-tagged FAQ set automatically, no URL param needed (native doesn't
  // reload the JS context between screens, so the store alone is reliable).
  const isAdhaJourney = useIsAdhaJourney();
  const { categories, flattened, isLoading } = useFaqs(isAdhaJourney);
  const hasSearch = searchText.trim().length > 0;

  const filteredItems = useMemo(() => {
    return searchFaqItems(flattened.items, searchText, {
      includeCategory: true,
      sortByCategory: true,
    });
  }, [flattened.items, searchText]);

  const hasData =
    (categories?.length || 0) > 0 && (flattened?.items?.length || 0) > 0;
  const hasSearchResults = filteredItems.length > 0;

  useCatalogDynatraceView(DT_SCREEN.FAQ, 'faq_viewed', !isLoading);
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.FAQ,
    ALMETLAA_ACTION.FAQ.VIEW,
    isAdhaJourney && !isLoading,
  );

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader title={t("profile.faq" as any)} />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom, 24) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        <View style={styles.content}>
          <FAQSearchInput
            value={searchText}
            onChangeText={setSearchText}
            inputRef={searchInputRef}
          />

          {/* Search Results */}
          {hasSearch ? (
            isLoading ? (
              <View style={styles.searchSkeletons}>
                {[...Array(3)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    height={64}
                    borderRadius={16}
                    style={{ width: "100%" }}
                  />
                ))}
              </View>
            ) : hasSearchResults ? (
              <FaqList items={filteredItems} />
            ) : (
              <FAQEmptyState isSearchNoResults={true} />
            )
          ) : !isLoading && !hasData ? (
            <FAQEmptyState />
          ) : (
            <>
              {/* Categories */}
              {/* No isRTL textAlign here (native-only screen): Typography's
                  native 'left' is mirrored to the right by the root RTL
                  direction; forcing 'right' double-flips Arabic to the left. */}
              <Typography variant="headline" color="primary">
                {t("profile.faqCategories" as any)}
              </Typography>

              {isLoading ? (
                <View style={styles.categoriesContainer}>
                  {[...Array(4)].map((_, idx) => (
                    <Skeleton
                      key={idx}
                      height={52}
                      borderRadius={12}
                      style={{ width: "100%" }}
                    />
                  ))}
                </View>
              ) : (
                <View style={styles.categoriesContainer}>
                  {categories.map((category) => (
                    <Pressable
                      key={category.category}
                      style={[
                        styles.categoryItem,
                        { 
                          backgroundColor: colors.backgroundLevel2,
                        },
                      ]}
                      onPress={() =>
                        router.push({
                          pathname: "/faq-category",
                          params: { category: category.category },
                        } as any)
                      }
                    >
                      <Typography
                        variant="headline"
                        color="primary"
                        style={styles.categoryText}
                      >
                        {category.category}
                      </Typography>
                      <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                        <ChevronRightIcon
                          size={24}
                          color={colors.fontPrimary}
                        />
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </>
          )}

          {/* Bottom section */}
          <View style={styles.bottomSection}>
            <Typography
              variant="headline"
              color="primary"
              style={styles.bottomTitle}
            >
              {t("profile.faqCantFind" as any)}
            </Typography>
            <Pressable
              style={styles.raiseRequestButton}
              onPress={() => router.push("/service-request-create" as any)}
            >
              <Typography
                variant="headline"
                color="primary"
                style={styles.raiseRequestText}
              >
                {t("profile.faqRaiseRequest" as any)}
              </Typography>
              <View
                style={[
                  styles.underline,
                  { backgroundColor: colors.buttonDefault },
                ]}
              />
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingTop: 32,
    paddingHorizontal: 16,
    gap: 32,
  },
  scrollContent: {
    paddingBottom: 32,
    flexGrow: 1,
  },
  categoriesContainer: {
    gap: 12,
  },
  searchSkeletons: {
    gap: 12,
  },
  categoryItem: {
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  categoryText: {
    // Typography component handles font styling
  },
  bottomSection: {
    alignItems: "center",
    gap: 17,
    marginTop: "auto",
    paddingBottom: 24,
  },
  bottomTitle: {
    textAlign: "center",
  },
  raiseRequestButton: {
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  raiseRequestText: {
    marginBottom: 2,
  },
  underline: {
    height: 1,
    width: "100%",
  },
});
